import Phaser from 'phaser';

// === PANNEAU SCROLLABLE RÉUTILISABLE ===
// Encapsule un container Phaser avec masque, scroll molette et drag.
// Usage :
//   const panel = new ScrollablePanel(scene, 0, headerHeight, 800, viewHeight);
//   panel.add(scene.add.text(...));  // ajouter du contenu
//   panel.setContentHeight(totalY);  // définir la hauteur totale du contenu
//   panel.destroy();                 // OBLIGATOIRE dans shutdown() de la scène

export class ScrollablePanel {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  private viewTop: number;
  private viewHeight: number;
  private viewWidth: number;
  private contentHeight = 0;
  private scrollY = 0;
  private isDragging = false;
  private dragStartY = 0;
  private dragStartScroll = 0;

  // Références stockées pour pouvoir retirer les listeners proprement
  private _onWheel: (_p: Phaser.Input.Pointer, _g: any, _dx: number, dy: number) => void;
  private _onPointerDown: (pointer: Phaser.Input.Pointer) => void;
  private _onPointerMove: (pointer: Phaser.Input.Pointer) => void;
  private _onPointerUp: () => void;

  constructor(scene: Phaser.Scene, x: number, viewTop: number, viewWidth: number, viewHeight: number) {
    this.scene = scene;
    this.viewTop = viewTop;
    this.viewWidth = viewWidth;
    this.viewHeight = viewHeight;

    this.container = scene.add.container(x, viewTop).setDepth(1);

    // Masque de clipping
    const mask = scene.add.rectangle(
      x + viewWidth / 2, viewTop + viewHeight / 2,
      viewWidth, viewHeight, 0x000000
    ).setVisible(false);
    this.container.setMask(mask.createGeometryMask());

    // Initialisation des handlers (pour pouvoir les retirer plus tard)
    this._onWheel = (_p, _g, _dx, dy) => this.scrollBy(dy * 0.5);
    this._onPointerDown = (pointer) => {
      if (pointer.y >= this.viewTop && pointer.y <= this.viewTop + this.viewHeight) {
        this.isDragging = true;
        this.dragStartY = pointer.y;
        this.dragStartScroll = this.scrollY;
      }
    };
    this._onPointerMove = (pointer) => {
      if (this.isDragging) {
        const dy = this.dragStartY - pointer.y;
        this.scrollY = this.dragStartScroll + dy;
        this.clamp();
        this.container.y = this.viewTop - this.scrollY;
      }
    };
    this._onPointerUp = () => { this.isDragging = false; };

    this.setupInput();
  }

  /** Ajouter un GameObject au panneau scrollable */
  add(obj: Phaser.GameObjects.GameObject): void {
    this.container.add(obj);
  }

  /** Définir la hauteur totale du contenu (appeler après avoir tout ajouté) */
  setContentHeight(height: number): void {
    this.contentHeight = height;
  }

  /** Retourner le container pour accès direct si besoin */
  getContainer(): Phaser.GameObjects.Container {
    return this.container;
  }

  /** Nettoyer les listeners — à appeler dans le shutdown() de la scène parente */
  destroy(): void {
    const input = this.scene.input;
    input.off('wheel', this._onWheel);
    input.off('pointerdown', this._onPointerDown);
    input.off('pointermove', this._onPointerMove);
    input.off('pointerup', this._onPointerUp);
  }

  private setupInput(): void {
    const input = this.scene.input;
    input.on('wheel', this._onWheel);
    input.on('pointerdown', this._onPointerDown);
    input.on('pointermove', this._onPointerMove);
    input.on('pointerup', this._onPointerUp);
  }

  private scrollBy(amount: number): void {
    this.scrollY += amount;
    this.clamp();
    this.container.y = this.viewTop - this.scrollY;
  }

  private clamp(): void {
    const max = Math.max(0, this.contentHeight - this.viewHeight);
    this.scrollY = Phaser.Math.Clamp(this.scrollY, 0, max);
  }
}
